import type { BenefitSummary, BenefitsData } from "@yutai/shared"
import { Link, useParams } from "react-router-dom"
import useSWR from "swr"
import { CategoryBadge } from "../../components/CategoryBadge.js"
import { SiteFooter } from "../../components/SiteFooter.js"
import { googleSearchUrl, yahooFinanceJpUrl } from "../../lib/financeLinks.js"
import { formatDeliveryMonths } from "../../lib/formatMonths.js"
import {
  RELEVANCE_LEVEL_DESCRIPTIONS,
  RELEVANCE_LEVEL_LABELS,
  relevanceLevelFromScore,
} from "../../lib/relevance.js"
import styles from "./CompanyPage.module.css"

const R2_URL = import.meta.env.VITE_R2_PUBLIC_URL ?? ""

async function fetcher(url: string): Promise<BenefitSummary[]> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)
  const data: BenefitsData = await res.json()
  return data.items.map((item) => ({
    ...item,
    deliveryMonths: item.deliveryMonths ?? [],
    title: item.title ?? "",
    businessDescription: item.businessDescription ?? "",
    benefitRelevanceScore: item.benefitRelevanceScore ?? 0,
    approxStockPrice: item.approxStockPrice ?? null,
    marketCap: item.marketCap ?? null,
    dividendYield: item.dividendYield ?? null,
    benefitYield: item.benefitYield ?? null,
    financialsUpdatedAt: item.financialsUpdatedAt ?? null,
  }))
}

export function CompanyPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useSWR<BenefitSummary[]>(`${R2_URL}/benefits.json`, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 3600_000,
  })

  const benefit = data?.find((b) => b.id === id)
  const relevanceLevel = benefit ? relevanceLevelFromScore(benefit.benefitRelevanceScore) : null

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.backLink}>
            ← 一覧に戻る
          </Link>
          <span className={styles.siteName}>株主優待ナビ</span>
        </div>
      </header>

      <main className={styles.main}>
        {isLoading && <div className={styles.loading}>読み込み中...</div>}
        {error && <div className={styles.error}>データの取得に失敗しました。</div>}
        {!isLoading && !error && !benefit && (
          <div className={styles.notFound}>
            <p>企業が見つかりませんでした。</p>
            <Link to="/">一覧に戻る</Link>
          </div>
        )}

        {benefit && (
          <article className={styles.article}>
            <div className={styles.companyHeader}>
              <div className={styles.companyMeta}>
                <a
                  href={yahooFinanceJpUrl(benefit.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.codeLink}
                >
                  {benefit.id}
                </a>
                <span className={styles.industry}>| {benefit.industry}</span>
              </div>
              <h1 className={styles.companyName}>{benefit.name}</h1>
            </div>

            {benefit.businessDescription && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>事業内容</h2>
                <p className={styles.description}>{benefit.businessDescription}</p>
              </section>
            )}

            <div className={styles.categories}>
              {benefit.categories.map((cat) => (
                <CategoryBadge key={cat} category={cat} />
              ))}
            </div>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>優待概要</h2>
              <p className={styles.description}>{benefit.description}</p>
            </section>

            {benefit.tiers.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>株数別優待内容</h2>
                <div className={styles.tiers}>
                  {benefit.tiers.map((tier, i) => (
                    <div key={`${tier.minimumShares}-${i}`} className={styles.tier}>
                      <div className={styles.tierShares}>
                        {tier.minimumShares != null
                          ? `${tier.minimumShares.toLocaleString()}株以上`
                          : "株数不明"}
                      </div>
                      <p className={styles.tierDesc}>{tier.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>基本情報</h2>
              <dl className={styles.dl}>
                <dt>権利確定月</dt>
                <dd>{benefit.recordMonths.map((m) => `${m}月`).join("・")}</dd>
                {benefit.deliveryMonths.length > 0 && (
                  <>
                    <dt>優待送付月</dt>
                    <dd>{formatDeliveryMonths(benefit.deliveryMonths)}</dd>
                  </>
                )}
                <dt>最低株数</dt>
                <dd>{benefit.minimumShares.toLocaleString()}株</dd>
                {benefit.minimumBenefitValue != null && (
                  <>
                    <dt>優待金額目安</dt>
                    <dd>{benefit.minimumBenefitValue.toLocaleString()}円相当〜</dd>
                  </>
                )}
                {benefit.holdingYears.length > 0 && (
                  <>
                    <dt>長期保有条件</dt>
                    <dd>{benefit.holdingYears.map((y) => `${y}年以上`).join("・")}から特典</dd>
                  </>
                )}
              </dl>
            </section>

            {relevanceLevel && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>事業と優待の関連</h2>
                <p className={styles.description}>
                  事業関連度: {RELEVANCE_LEVEL_LABELS[relevanceLevel]}（
                  {RELEVANCE_LEVEL_DESCRIPTIONS[relevanceLevel]}）
                </p>
              </section>
            )}

            <div className={styles.footer}>
              <div className={styles.footerLinks}>
                <a
                  href={yahooFinanceJpUrl(benefit.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  Yahoo!ファイナンスで株価を見る ↗
                </a>
                <a
                  href={benefit.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  公式ページで確認する ↗
                </a>
                <a
                  href={googleSearchUrl(benefit.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  優待ブログ検索 ↗
                </a>
              </div>
              <span className={styles.updated}>
                更新: {new Date(benefit.updatedAt).toLocaleDateString("ja-JP")}
              </span>
            </div>
          </article>
        )}
      </main>

      <SiteFooter />
    </div>
  )
}
